import { useState, useMemo } from 'react';
import { LifeBuoy, Search, ChevronRight, Download, FileText, HelpCircle, Play, Book, Zap, Shield, Layers, Upload, Sparkles, Users, TrendingUp, Cog, UserCog, Cookie, Wallet, ShieldCheck, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { SafeText } from '@/components/SafeText';

const GUIDES = [
  {
    id: 'getting-started',
    icon: Play,
    title: 'Getting Started',
    color: 'from-emerald-500 to-teal-500',
    steps: [
      {
        title: '1. Prepare your data',
        body: 'DataRefinery accepts CSV, Excel (.xlsx / .xls), and JSON. At minimum you need columns for date, supplier, description, and amount. Extra columns like PO number, GL account, cost center are auto-mapped from 70+ known ERP aliases. Download the **Recommended template** on the Upload page for the full 18-column schema.',
      },
      {
        title: '2. Check & upload your file',
        body: 'Go to **Upload** in the left sidebar, drag your file into the drop zone (or click to browse) and click **Check file**. The **Preflight Check** scans every row for duplicates, ambiguous or unreadable dates, placeholders, decimal artifacts in codes and more — nothing is stored yet. Review the issue cards, keep **Auto-fix safe issues** on, then **Confirm upload** (or Cancel and fix the file). If DataRefinery detects >50% overlap with an existing session, you get an "Upload Anyway" override.',
      },
      {
        title: '3. Wait for AI cleansing',
        body: 'The system automatically de-duplicates suppliers, splits multi-item transactions, and flags cash payments. A quality score (0-100) appears on the session card. Records with issues are surfaced in the **Corrections** page.',
      },
      {
        title: '4. Classify with Vocabulary Lock',
        body: 'On the **Processing Results** page, hit **Classify with Vocabulary Lock**. Stage 0 designs a canonical L1→L4 taxonomy from a sample. Stage 1 forces every batch to use that taxonomy. Stage 2 reconciles fuzzy duplicates and flags hierarchy collisions.',
      },
      {
        title: '5. Review flagged records',
        body: 'Open **Corrections → Needs Review** tab. Use the **Hierarchy Collision Resolver** to one-click merge any label used at both L2 and L3. Use **AI Suggestions** to fix low-confidence records.',
      },
      {
        title: '6. Export or push downstream',
        body: 'On Processing Results → **Export CSV** for downstream apps (SpendLens, BI tools) or **Download PDF Report** for stakeholders. Or hit the **/api/external/classified-data** endpoint to pull programmatically.',
      },
    ],
  },
  {
    id: 'preflight',
    icon: ShieldCheck,
    title: 'Preflight Check (2-Step Upload)',
    color: 'from-rose-500 to-red-500',
    steps: [
      { title: 'How the 2-step upload works', body: 'Every manual upload (New Session, Add to Existing, Replace Existing) is **checked first** — nothing is stored until you confirm. Step 1: pick your file and click **Check file**. Step 2: review the summary and click **Confirm upload**, or **Cancel** and fix the file. Cancelling leaves no trace in your data.' },
      { title: 'Reading the summary', body: 'The headline shows rows checked plus the count of **Blocking**, **Warning** and **Info** issues. Each card names the column (using your own header), how many rows are affected, up to 5 example cells with their spreadsheet row number, and a plain-English **How to fix**.' },
      { title: 'What blocks an upload', body: '**Exact duplicate rows**, **ambiguous dates** (e.g. 07-06-2023 could be 7 June or 6 July), **mixed day/month order** in one column, **unreadable dates**, and **missing required columns** (supplier, description, amount). Confirm is disabled until these are resolved, auto-fixed or overridden.' },
      { title: 'Warnings & info', body: 'Warnings: Excel serial dates (e.g. 44192), mixed date formats, codes stored as decimals ("287.0"), placeholders (N/A, TBD, -), blank required values, non-numeric amounts, empty columns, garbled characters (encoding), several columns competing for one field, and category inconsistencies. Info: negative amounts, inferred date order, extra spaces, unrecognised columns, partly filled categories.' },
      { title: 'Auto-fix safe issues', body: 'Leave the **Auto-fix safe issues** toggle on (default) to trim extra spaces, strip ".0" from codes, convert Excel serial dates to YYYY-MM-DD, drop empty columns and drop exact duplicate rows (the first copy is kept). Your original file is never changed — fixes apply only to what is stored.' },
      { title: 'Overriding a blocking issue', body: 'If you are sure the data is correct, tick **Override and upload anyway** and enter a reason (min. 5 characters, e.g. "Dates confirmed DD-MM with finance"). The override, reason and issue counts are recorded in the **audit log**.' },
      { title: 'Download the full report', body: '**Download full report** gives a CSV with one line per affected cell (severity, rule, row, column, value, how to fix) — hand it to whoever owns the source system so the next export is clean.' },
    ],
  },
  {
    id: 'keep-categories',
    icon: Tags,
    title: 'Keep My Categories',
    color: 'from-lime-500 to-emerald-600',
    steps: [
      { title: 'When to use it', body: 'Choose **Keep my categories** on the Upload page when your file already has L1-L4 spend categories you want to keep. Your data is still fully cleansed; only the classification step changes.' },
      { title: 'Column names it recognises', body: '"Category L1"-"Category L4", "L1"-"L4", "Level 1"-"Level 4", "L1 Category", "Cat L1" and similar. Uploaded values are always kept as **Source Category L1-L4** columns in Processed Data, whichever mode you choose.' },
      { title: 'What happens to each row', body: '**Fully categorised rows** keep your categories, skip the AI and get 100% confidence. **Partly categorised rows**: the AI fills only the missing levels — your values are never changed. **Uncategorised rows**: the AI classifies them using your own category names; anything that doesn\'t fit gets a new category and is flagged **Needs Review**.' },
      { title: 'Lower AI cost', body: 'Your AI budget is only charged for rows the AI actually processes. A file that is fully categorised is classified instantly with no AI usage.' },
      { title: 'Preflight category checks', body: 'The preflight check warns when the same category sits under two different parents (e.g. "Hardware" under both IT and Office), when a level is skipped (L3 filled, L2 blank), and tells you how many rows are only partly categorised. If you choose this mode but no category columns are found, you\'ll see a warning before confirming.' },
      { title: 'Other modes', body: 'With **AI Generated**, **UNSPSC** or **Bespoke**, uploaded categories are stored for reference but the AI re-classifies every row. Retries, Vocabulary Lock reclassification and **Add to Existing** uploads remember Keep my categories for sessions that used it.' },
    ],
  },
  {
    id: 'connectors',
    icon: Zap,
    title: 'Connect an ERP or Auto-Sync',
    color: 'from-blue-500 to-indigo-500',
    steps: [
      {
        title: 'Option A — Native QuickBooks OAuth',
        body: 'Open **Connectors**, find the QuickBooks card, click **Connect QuickBooks**. Authorise on Intuit\'s page, come back to DataRefinery, hit **Sync now**. Bills + Purchases flow in automatically. Sync any time; incremental updates only pull new records.',
      },
      {
        title: 'Option B — Generic ingest API (any ERP)',
        body: 'On **Connectors**, click **New Connector**, pick your source type (SAP, Oracle, NetSuite, Ariba, etc.), give it a name. DataRefinery generates a per-connector API key. Your ERP script POSTs to `/api/ingest/{connector_id}` with `X-API-Key` header + JSON records. Turn on **Auto-process** to cleanse + classify on every push.',
      },
      {
        title: 'Column mapping',
        body: 'When creating a connector, set a Field Mapping (e.g. `{"vendor_name": "supplier", "invoice_amt": "amount"}`) so your ERP\'s column names get translated into DataRefinery\'s schema on ingestion.',
      },
    ],
  },
  {
    id: 'taxonomy',
    icon: Layers,
    title: 'Manage Your Taxonomy',
    color: 'from-amber-500 to-orange-500',
    steps: [
      {
        title: 'Choose your mode',
        body: 'Four taxonomy modes: (a) **AI-generated** (default) — Stage 0 builds a bespoke L1-L4 hierarchy from your data. (b) **UNSPSC** — international 4-level standard. (c) **Bespoke** — you define L1-L4 in the Category Taxonomy page and the AI is forced to route into it. (d) **Keep my categories** — use the L1-L4 already in your file; the AI only fills missing levels and classifies uncategorised rows with your vocabulary.',
      },
      {
        title: 'Set classification rules',
        body: 'On the **Rules** page, create rules to pin specific suppliers or keywords to specific L1-L4 paths. Rules apply in priority order (lower = higher priority) before the LLM runs. Example: `supplier_match: IBRAHIM FIBRES LIMITED → Raw Materials › Manmade Staple Fibers › Polyester Staple Fiber (PSF)`.',
      },
      {
        title: 'Export & import Taxonomy Packs',
        body: 'On **Supplier Normalization → Taxonomy Pack**, export the current canonical vocabulary as JSON. Import into another tenant (or backup before an experiment). Great for cross-tenant standardization or disaster recovery.',
      },
      {
        title: 'Fix drift & collisions',
        body: 'After classification, check the **Supplier Drift Detector** banner on Processing Results (flags suppliers whose L1 changed vs history). Use one-click **Pin to Historical** to lock. Use the **Hierarchy Collision Resolver** on Corrections → Needs Review for label collisions.',
      },
    ],
  },
  {
    id: 'corrections',
    icon: Cog,
    title: 'Corrections & Human-in-the-Loop',
    color: 'from-teal-500 to-cyan-500',
    steps: [
      { title: 'Filter by tab', body: 'All Records / Needs Correction (AI-flagged) / Needs Review (Vocabulary Lock flags) / Corrected. Each tab has a live count.' },
      { title: 'Per-row correction', body: 'Click a record row to expand. Edit L1-L4, supplier, add notes. Submit — the correction feeds ML performance metrics.' },
      { title: 'Bulk correction', body: 'Select multiple rows via checkboxes → **Correct N selected** to update them all at once.' },
      { title: 'AI suggestions', body: 'Blue banner shows records the LLM has flagged with reasoning. One-click Accept applies the suggested path.' },
      { title: 'Hierarchy collision resolver', body: 'On Needs Review tab, top card lists labels used at both L2 and L3. One-click keep_at_l2 / promote_to_l3 / dismiss.' },
    ],
  },
  {
    id: 'suppliers',
    icon: Users,
    title: 'Supplier Normalization',
    color: 'from-purple-500 to-pink-500',
    steps: [
      {
        title: 'Review fuzzy matches',
        body: 'Open **Supplier Normalization**. Pairs with ≥85% RapidFuzz similarity appear as merge candidates. Merge (renames variant → canonical) or Do Not Merge (blocks pair from future suggestions).',
      },
      {
        title: 'Bulk block by threshold',
        body: 'Set a similarity threshold (e.g. 88%) and hit **Block from suggestions** to bulk-dismiss all pairs below it. A confirmation modal shows a preview of what will be blocked — remember, this does NOT merge them together, it prevents them from re-appearing.',
      },
      {
        title: 'Undo mistakes',
        body: 'Bulk blocks are backed up to `supplier_no_merge_backups`. If you accidentally bulk-block genuinely-duplicate pairs, ask your admin to reverse it — every batch is reversible.',
      },
    ],
  },
  {
    id: 'privacy',
    icon: Shield,
    title: 'Privacy, GDPR & Security',
    color: 'from-rose-500 to-red-500',
    steps: [
      {
        title: 'Enable 2-factor authentication',
        body: 'Open **Privacy & Data → Two-factor authentication**. Click **Set up authenticator**, scan the QR code with Google Authenticator / 1Password / Authy, enter the 6-digit code to confirm. Admin accounts require MFA (cannot disable).',
      },
      {
        title: 'Download your data (GDPR Art. 15 & 20)',
        body: 'On **Privacy & Data → Download my data**, get a ZIP of every record, correction, rule, connector, and audit entry linked to your account — in JSON + CSV with a manifest.',
      },
      {
        title: 'Scan and mask PII',
        body: 'On **Privacy & Data → PII scanner**, pick a session and hit **Scan**. Detects 8 patterns (credit card, email, phone, IBAN, SSN, CNIC, Aadhaar, NINo). Click **Redact all** to mask detected PII — originals are backed up (encrypted) for reversibility.',
      },
      {
        title: 'Delete your account (Right to Erasure)',
        body: 'On **Privacy & Data → Delete my account**, type the exact confirm phrase and confirm. Cascades across 16 collections. A PII-safe tombstone is retained in `gdpr_erasure_log` for compliance proof.',
      },
      {
        title: 'Cookie preferences',
        body: 'On first visit, a persistent bottom banner asks you to accept cookies. **Essential Only** stores nothing beyond the session token used to keep you signed in. **Accept** additionally enables functional + analytics categories (reserved for future features — nothing is collected today). Change your mind any time via the **Cookie preferences** link in the login-page footer.',
      },
    ],
  },
  {
    id: 'reports',
    icon: TrendingUp,
    title: 'Analytics & Reports',
    color: 'from-cyan-500 to-blue-500',
    steps: [
      {
        title: 'Dashboard overview',
        body: 'Landing page shows total spend, active suppliers, classification %, L1 distribution, and Trust Score. Use the session dropdown to filter by upload session or view cumulative all-time metrics.',
      },
      {
        title: 'Generate a PDF report',
        body: 'On **Reports**, pick a session (or cumulative), hit **Generate Report**. Includes AI Recommendations from GPT-5.2 analyzing your classification and suggesting improvements. Cached per session so re-runs are instant.',
      },
      {
        title: 'ML Insights',
        body: 'Track classification accuracy over time and see which categories learn fastest from your corrections on the **ML Insights** page.',
      },
    ],
  },
  {
    id: 'ai-budget',
    icon: Wallet,
    title: 'AI Budget & Abuse Defense',
    color: 'from-lime-500 to-emerald-500',
    steps: [
      {
        title: 'How your monthly LLM budget works',
        body: 'Every tenant has a monthly Universal LLM Key budget (default set on the organization record — visible to Super Users in **Platform Admin → Organizations**). Each classification, enrichment, or AI recommendation charges the tenant\'s counter. When the counter hits the cap, AI-powered endpoints return a **402 Payment Required** with a clear message; the rest of the app (upload, corrections, exports, reports) keeps working. The counter resets on the 1st of each month (UTC).',
      },
      {
        title: '3-layer abuse defense (transparent to you)',
        body: 'Layer 1 — **endpoint rate limits** (per IP per minute) block bursts from a single client. Layer 2 — **per-user burst caps** stop one user from monopolizing tenant budget. Layer 3 — **atomic per-tenant monthly cap** guarantees you never spend more than the configured USD budget, even under race conditions. All three layers activate silently unless a limit is hit, in which case you\'ll see a clear "rate limit" or "budget exceeded" toast.',
      },
      {
        title: 'What happens when the budget is exhausted',
        body: 'DataRefinery halts the run cleanly — no half-classified batches, no silent stalls. On Processing Results you\'ll see a **Resume Classification** button. Ask your Super User (or update the org budget) to top up, then hit Resume — the pipeline picks up exactly where it stopped, using cached partial work.',
      },
      {
        title: 'Reviewing usage',
        body: 'Super Users: **Platform Admin → Usage** shows LLM spend per tenant this month, plus a bar that turns amber >60% of budget and red >90%. Tenant Admins: watch the **AI Recommendations** panel on Reports — that cost is the smallest per-run item; classification and enrichment are the two heaviest consumers.',
      },
      {
        title: 'Raising the budget',
        body: 'Super User: open **Platform Admin → Organizations**, edit the row, raise `monthly_llm_budget_usd`. Change takes effect immediately. Universal LLM Key top-ups themselves are done in the Emergent profile (Profile → Manage Plan → Universal Key → Add Balance).',
      },
    ],
  },
  {
    id: 'cookie-consent',
    icon: Cookie,
    title: 'Cookie Consent & Preferences',
    color: 'from-yellow-500 to-amber-500',
    steps: [
      {
        title: 'What you see on first visit',
        body: 'A persistent banner sits at the bottom of the login page with a shield icon and two buttons: **Essential Only** and **Accept**. The banner is dismiss-resistant — you must pick one before continuing to sign in. Your decision is stored in your browser (`localStorage` under `dr_cookie_consent_v2`).',
      },
      {
        title: 'Essential Only',
        body: 'Stores nothing beyond the session token that keeps you signed in and a CSRF-safety marker. No tracking, no analytics, no third-party cookies. This is the strictest option and DataRefinery works fully with it.',
      },
      {
        title: 'Accept (all categories)',
        body: 'Same essentials, plus flips **Functional** (remembered UI preferences like sidebar collapsed / theme — reserved for future features) and **Analytics** (anonymous, aggregated usage metrics — also reserved for future features) to "on". No Marketing / advertising cookies are ever set — that category is permanently off.',
      },
      {
        title: 'Changing your mind',
        body: 'Sign out to return to the login page and click **Cookie preferences** in the footer under the sign-in form. The banner re-appears; pick the other option and it\'s saved immediately.',
      },
      {
        title: 'For legal / privacy officers',
        body: 'The consent record includes an ISO-8601 `decided_at` timestamp, the chosen option (`essential_only` | `accept_all`), and a category boolean map — enough to prove GDPR/CCPA-style prior consent per user. The banner never appears on the logged-in app (once signed in, only essentials are ever used).',
      },
    ],
  },
  {
    id: 'user-management',
    icon: Users,
    title: 'Invite & Manage Users (Tenant Admin)',
    color: 'from-sky-500 to-blue-500',
    steps: [
      {
        title: 'Who this section is for',
        body: '**Admins** (and Super Users acting inside a tenant) see the **User Management** page in the left sidebar. This is where you invite teammates, resend invites, change roles, and remove users **inside your own tenant**. Analyst and Viewer roles do not see this page. Super Users additionally see a cross-tenant version at Platform Admin → Users.',
      },
      {
        title: '1. Invite a new user (the easy way)',
        body: 'Open **User Management → Invite New User**. Fill in Full Name, Organization (optional — defaults to yours), Email, and pick a Role (Admin / Analyst / Viewer). Leave the **Auto-generate temporary password & email invite** checkbox ticked (default, recommended). Click **Invite & Email User**. DataRefinery generates a 14-character temporary password, hashes it, stores the user record, and emails them a branded invite via Resend with sign-in instructions. They\'re forced to change the temp password on first login. You never see or handle the raw password.',
      },
      {
        title: '2. Invite with a manual password',
        body: 'Prefer to type the temp password yourself? Uncheck the auto-generate toggle — a text input appears. Type any string of 8+ chars, hit Invite. You are responsible for sharing this password with the user via a secure channel (Signal, 1Password, in-person). They are still forced to change it on first login, because any shared secret should be rotated.',
      },
      {
        title: '3. What if email delivery fails?',
        body: 'Resend can reject an invite for several reasons — unverified sender domain, rate limit, bounced address, restricted API key. When that happens, the toast turns **amber** and displays the temporary password for 30 seconds so you can copy it and share it manually. If you miss the toast, no problem: use the **Resend invite** button (see next step) to try again with a fresh password.',
      },
      {
        title: '4. Resend an invite (envelope icon)',
        body: 'A user misplaced their invite email or their password stopped working? Find them in the users table and click the **envelope icon** in their row. Confirm the warning — their current password is invalidated **immediately**, a new 14-char temp is generated and emailed to them, and they must change it on first login. Works on both users who never signed in and existing active users. Audit-logged as `user_invite_resent`.',
      },
      {
        title: '5. Seat limits — why invites sometimes fail',
        body: 'Every tenant has a **seat limit** set by your Super User (default: 5 seats). Each active (non-suspended) user in your tenant consumes one seat. When you hit the cap, new invites return a 403 error with a clear message telling you to contact your Super User. Suspended users do **not** consume a seat — reinstating a suspended user does. If you legitimately need more capacity, message your Super User and they can raise the seat cap in seconds from Platform Admin.',
      },
      {
        title: '6. Change a role',
        body: 'Click the **role badge** on any user\'s row — a dropdown appears with Admin / Analyst / Viewer. You cannot assign Super User from this panel (only the platform Super User can). Users see the new permissions immediately on their next request. Role changes are audit-logged as `role_change`.',
      },
      {
        title: '7. Delete a user',
        body: 'Red trash icon on the row. Confirm the warning — the user record is soft-deleted (a compliance-safe tombstone is retained). You cannot delete your own account. Spend records they created stay in your tenant. To also delete their derived data (spend, corrections), ask your Super User to run a hard-delete via the maintenance script.',
      },
    ],
  },
  {
    id: 'platform-admin',
    icon: UserCog,
    title: 'Platform Admin (Super User)',
    color: 'from-purple-500 to-fuchsia-500',
    steps: [
      {
        title: 'Who this section is for',
        body: 'Only accounts with role **super_user** can see the **Platform Admin** tab in the left sidebar (purple icon). Super Users create tenants, set seat limits, assign roles across tenants, monitor platform-wide usage, and can impersonate any user for support. Regular Admins manage users **inside their own tenant only** — see **User Management** in the sidebar for that.',
      },
      {
        title: '1. Create a new organization (tenant)',
        body: 'Open **Platform Admin → Organizations → New Organization**. Enter the org name, pick a plan (Starter / Growth / Enterprise), set the monthly upload limit, and the **seat limit** (1–10,000, default 5 — this is the max number of active users the tenant may have). Then fill the **Owner account** section (email, full name, initial role — normally Admin). Click **Create & Invite**. DataRefinery provisions the owner user with a 14-char temp password and emails them a branded invite via Resend. If email delivery fails, the temp password is shown in a toast so you can share it manually. The owner must change the password on first login.',
      },
      {
        title: '2. Seat limits — control tenant capacity',
        body: 'Every organization row shows a **Seats** column with `active / limit`. **Click the number** to edit inline (Enter saves, Escape cancels). Turns **red** when the tenant is at capacity — new invites from tenant admins are blocked with a 403 until you raise the cap or an active member is removed / suspended. Suspended users do **NOT** consume a seat. If you drop the limit below current active count, you get a confirm() warning — the change goes through, but tenant admins can\'t invite anyone else until active count catches down. Seat limit is independent of billing plan — you can give an Enterprise tenant a Starter-sized cap for a pilot.',
      },
      {
        title: '3. Change plan inline',
        body: 'Organizations tab → click the plan dropdown on any row to switch between Starter / Growth / Enterprise / Legacy. Applies immediately, no confirm needed. Today the plan name is a label used for reporting and grouping — real feature gating (per-tier limits) is on the roadmap.',
      },
      {
        title: '4. Manage users across all tenants',
        body: 'The **Users** tab lists every user on the platform with their tenant, role, and status. Search by email or name. Change any user\'s role via the inline dropdown (super_user / admin / analyst / viewer). Suspend a user with the Ban icon — suspended users get a 403 on every API call until reinstated. You cannot demote yourself from super_user and you cannot suspend yourself.',
      },
      {
        title: '5. Resend invite (cross-tenant)',
        body: 'On the Users tab, click the **envelope icon** (between Impersonate and Ban) on any row. Rotates that user\'s password to a fresh 14-char temp and re-emails the invite — works across any tenant, not just your own. Suspended users are excluded (icon is disabled/grey) — reinstate them first so you\'re not accidentally reviving a decommissioned account. On email failure the temp password is shown to you so you can share it out-of-band. Audit-logged as `super_user_invite_resent` with the target tenant recorded.',
      },
      {
        title: '6. Monitor platform usage',
        body: 'The **Usage** tab shows four KPIs at the top — total organizations, users, uploads, and spend across all tenants — plus MFA compliance and role distribution. The per-tenant table below shows members, uploads, records, total spend, and last-activity date for each org. Use this to spot tenants approaching their monthly limits or going idle.',
      },
      {
        title: '7. Impersonate a user (for support)',
        body: 'On the Users tab, click the **Log-in icon** next to any user, confirm the warning, and DataRefinery mints a **30-minute impersonation token**. A purple **"Impersonating {email}"** banner appears at the top of every page. You see exactly what that user sees. Every action you take is audit-logged with your own super_user ID via the `impersonated_by` JWT claim. Click **Exit impersonation** in the banner to restore your original super_user session.',
      },
      {
        title: '8. Archive or reinstate an organization',
        body: 'Organizations tab → **red trash icon** archives an org (status=archived, every member suspended, no logins possible; data is retained). Filter Status = Archived, then click the **green circular-arrow icon** to reinstate — members suspended by the archive are un-suspended automatically; members who were individually suspended by an admin stay suspended.',
      },
      {
        title: '9. Security notes for super users',
        body: 'MFA is strongly recommended for super_user accounts — you have platform-wide access. All super_user actions are recorded in the immutable audit log (search category `super_admin`). Impersonation tokens expire after 30 minutes and cannot be extended — start a fresh impersonation if you need more time.',
      },
    ],
  },
];

const FAQS = [
  {
    q: 'How long does classification take?',
    a: 'Roughly 60-90 seconds per 60-record batch. A 10,000-record file typically finishes in 30-45 minutes. Progress is shown in real time on Processing Results.',
  },
  {
    q: 'What happens if the LLM budget runs out during classification?',
    a: 'DataRefinery detects budget exhaustion, halts the run cleanly (no silent stalls), flushes partial work, and shows a "Resume Classification" button. Top up your Universal Key and click Resume — it picks up exactly where it stopped.',
  },
  {
    q: 'Can I re-classify after correcting some records?',
    a: 'Yes. Use **Reclassify with Vocabulary Lock** on Processing Results — it re-runs Stage 0-1-2 with your corrections seeded as ground truth. Your corrections + tenant rules become part of the canonical vocabulary going forward.',
  },
  {
    q: 'What if two of my suppliers have very similar names but are different companies?',
    a: 'Use the per-row **Do Not Merge** button in Supplier Normalization. That pair will never be suggested again. Or set the bulk threshold higher (e.g. 92%) so only near-identical pairs appear as candidates.',
  },
  {
    q: 'How do I integrate with SpendLens / downstream apps?',
    a: 'Use the **External Pull API** (`GET /api/external/classified-data`) with a permission-scoped API key. Or export CSV and upload manually. The API supports pagination and filtering by upload session.',
  },
  {
    q: 'Is my data isolated from other tenants?',
    a: 'Yes. Every DB query is scoped by user_id / tenant_id across all endpoints. There is no cross-tenant read path. The Super User can see aggregated usage metrics per tenant but cannot read a tenant\'s classified records without explicit impersonation (which is audit-logged with a visible banner).',
  },
  {
    q: 'What data is encrypted at rest?',
    a: 'App-layer Fernet encryption on MFA secrets, password reset emails, PII masking backups, and QBO OAuth tokens. Full DB is also encrypted at the storage layer via MongoDB Atlas / disk encryption.',
  },
  {
    q: 'How do I invite team members?',
    a: 'It depends on your role: (1) **Super User** — go to Platform Admin → Organizations → New Organization to create a whole new tenant with an owner-admin (set the seat limit while you\'re at it). To add users to an existing tenant, use the Users tab and change a user\'s tenant via the API. (2) **Tenant Admin** — go to User Management → Invite New User, leave "Auto-generate temporary password & email invite" ticked, click Invite. They receive a branded email with a 14-char temp password and are forced to change it on first login. If your tenant is at its seat limit, ask your Super User to raise the cap.',
  },
  {
    q: 'What is a Super User and how is it different from an Admin?',
    a: 'A **Super User** manages the entire platform (multiple tenants). A **Tenant Admin** manages users and settings within a single tenant only. Super Users can: create/archive organizations, change any user\'s role across tenants, view cross-tenant usage analytics, and impersonate any user for support (30-min audit-logged sessions). Only the Super User sees the purple **Platform Admin** menu item in the sidebar.',
  },
  {
    q: 'When Super User creates a new organization, what does the owner get?',
    a: 'The new owner receives an invite email from DataRefinery (via Resend) with a one-time temporary password. They sign in at the standard login page, are prompted to change the password, and should enable MFA immediately. If email delivery fails, the Super User sees the temp password in a fallback toast and can share it manually via a secure channel.',
  },
  {
    q: 'What does "impersonation" actually do — can it be abused?',
    a: 'Impersonation lets a Super User act as any tenant user for up to 30 minutes to troubleshoot issues. The JWT includes an `impersonated_by` claim, so every action is attributed back to the Super User in the audit log (`super_admin` category). A visible purple banner sits above the app the entire time. Impersonating another Super User is blocked, as is impersonating a suspended user. Tokens cannot be extended — expire after 30 min.',
  },
  {
    q: 'What ERPs does DataRefinery support?',
    a: 'Native OAuth for QuickBooks Online (available). Generic push API for SAP, Oracle, NetSuite, Concur, Amex/Visa P-Card, Coupa, Ariba, Expensify, and any REST-capable ERP. Native connectors for SAP/Oracle/NetSuite are on the roadmap.',
  },
  {
    q: 'Can I use my own taxonomy (like our company\'s existing spend categories)?',
    a: 'Yes, two ways. If your file already contains L1-L4 columns, choose **Keep my categories** on Upload — fully categorised rows skip the AI and partial rows only get their gaps filled. Or, on **Category Taxonomy**, create your bespoke L1-L4 hierarchy and choose "Bespoke" mode. You can also import a Taxonomy Pack JSON from another instance.',
  },
  {
    q: 'If my file already has L1-L4 categories but is not cleansed, will it be cleansed and categorised?',
    a: 'It is always cleansed. Classification depends on the mode: with **Keep my categories** your L1-L4 are kept and the AI only fills gaps; with AI Generated, UNSPSC or Bespoke the AI re-classifies every row (your originals stay visible as Source Category columns).',
  },
  {
    q: 'Why was my upload blocked by the Preflight Check?',
    a: 'Blocking issues are exact duplicate rows, ambiguous or mixed day/month dates, unreadable dates and missing supplier/description/amount columns. Fix the file and re-check, turn on **Auto-fix** (removes duplicates), or tick **Override and upload anyway** with a reason — overrides are audit-logged.',
  },
  {
    q: 'Does the Preflight Check change my original file?',
    a: 'No. The check is read-only. Auto-fix only changes what DataRefinery stores, and you can download the full CSV report to correct the source system.',
  },
  {
    q: 'What happens if the AI classifies something wrong?',
    a: 'Three options: (1) manually correct on Corrections page — the correction feeds back into ML performance metrics, (2) create a classification rule (Rules page) to pin the supplier/keyword permanently, (3) use "Pin to historical" from the Drift Detector for supplier-level drift.',
  },
  {
    q: 'How is Trust Score calculated?',
    a: 'A 0-100 weighted score across 6 signals: field coverage, classification completeness, avg confidence, vocabulary stability (cross-session), supplier normalization coverage, and hierarchy consistency. See the tooltip on the Dashboard card for the breakdown.',
  },
  {
    q: 'Do I need to run classification for every new upload?',
    a: 'No. If you have Auto-Process on the connector, classification runs automatically on ingest. Manual uploads: the Upload page will auto-trigger classification if the session is fresh.',
  },
  {
    q: 'How do I roll back a bad classification?',
    a: 'Sessions are versioned. Use **Reclassify with Vocabulary Lock** to re-run cleanly with updated rules. To fully reset, delete the session (Undo last upload) and re-upload — original file is preserved.',
  },
  {
    q: 'What formats can I export in?',
    a: 'CSV (full data), branded PDF reports (with AI recommendations), JSON via the External Pull API, and full ZIP archives via GDPR Data Export.',
  },
  {
    q: 'What is the cookie banner asking me and can I refuse everything?',
    a: 'DataRefinery only uses essential cookies (a session token to keep you signed in). "Essential Only" gives full functionality — no tracking, no analytics. "Accept" additionally enables optional Functional + Analytics categories that are reserved for future features (nothing is actually collected today). Marketing cookies are permanently off. Change your choice any time via the "Cookie preferences" link in the login-page footer.',
  },
  {
    q: 'What is the AI budget and what happens when it runs out?',
    a: 'Each tenant has a monthly Universal LLM Key budget (in USD, visible to Super Users on the Organization record). Classification, enrichment, and AI recommendations debit that counter. When the cap is hit, AI endpoints return HTTP 402 with a clear message and the pipeline halts cleanly with a "Resume Classification" button. The rest of the app (upload, corrections, exports) keeps working. Counter resets on the 1st of each month UTC.',
  },
  {
    q: 'Why did my classification stop mid-run with a "rate limit" message?',
    a: 'DataRefinery has three abuse-defense layers: endpoint rate limits (per IP per minute), per-user burst caps, and the atomic monthly per-tenant budget. If you burst too many requests, layer 1 or 2 will pause you for a minute — retry then. If you hit layer 3, the tenant budget is exhausted; ask your Super User to top up.',
  },
];

const buildGuideMarkdown = () => {
  const lines = ['# DataRefinery — Complete How-To Guide\n',
    `> Generated ${new Date().toISOString().slice(0, 10)}\n`,
    '## Table of Contents\n'];
  GUIDES.forEach((g, i) => lines.push(`${i + 1}. [${g.title}](#${g.id})`));
  lines.push('\n---\n');
  GUIDES.forEach(g => {
    lines.push(`## ${g.title}\n`);
    g.steps.forEach(s => {
      lines.push(`### ${s.title}\n`);
      lines.push(`${s.body}\n`);
    });
    lines.push('\n---\n');
  });
  lines.push('## Frequently Asked Questions\n');
  FAQS.forEach(f => {
    lines.push(`### ${f.q}\n`);
    lines.push(`${f.a}\n`);
  });
  lines.push('\n---\n\n*For live support or to file a bug, contact your admin or reach the DataRefinery team.*\n');
  return lines.join('\n');
};

export const Help = () => {
  const [tab, setTab] = useState('guides');
  const [openGuide, setOpenGuide] = useState('getting-started');
  const [search, setSearch] = useState('');

  const filteredFaqs = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return FAQS;
    return FAQS.filter(f => f.q.toLowerCase().includes(s) || f.a.toLowerCase().includes(s));
  }, [search]);

  const downloadMarkdown = () => {
    const md = buildGuideMarkdown();
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DataRefinery_How_To_Guide_${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Markdown guide downloaded');
  };

  const downloadPdf = () => {
    // Use the browser's print-to-PDF via a Blob URL + iframe. Avoids
    // document.write (flagged as XSS-risky by static analyzers) and works
    // identically because the payload we generate is fully controlled.
    const md = buildGuideMarkdown();
    const bodyHtml = md
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/^---$/gm, '<hr>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^> (.+)$/gm, '<blockquote style="border-left:3px solid #cbd5e1;padding-left:12px;color:#64748b">$1</blockquote>');
    const html = `<!DOCTYPE html><html><head><title>DataRefinery How-To Guide</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 780px; margin: 40px auto; padding: 0 24px; color: #0f172a; line-height: 1.6; }
  h1 { border-bottom: 3px solid #1e40af; padding-bottom: 12px; color: #1e40af; }
  h2 { margin-top: 40px; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
  h3 { color: #334155; margin-top: 24px; }
  code { background: #f1f5f9; padding: 2px 6px; border-radius: 3px; font-size: 0.9em; }
  p { color: #334155; }
  hr { border: none; border-top: 1px solid #e2e8f0; margin: 32px 0; }
  @media print { body { max-width: none; } h2 { page-break-before: auto; } }
</style></head><body>${bodyHtml}</body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) setTimeout(() => { try { w.print(); } catch { /* user closed */ } }, 800);
  };

  const downloadBrandedPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help/how-to-guide.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DataRefinery_How_To_Guide_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Branded PDF downloaded');
    } catch (e) {
      toast.error('Failed to download branded PDF');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="help-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <LifeBuoy size={28} className="text-blue-600" />
            <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Help & Documentation</h1>
          </div>
          <p className="text-slate-600">
            Step-by-step guides, FAQs, and a downloadable how-to reference.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" onClick={downloadMarkdown} className="gap-2" data-testid="download-md-button">
            <FileText size={14} /> Markdown
          </Button>
          <Button variant="outline" onClick={downloadPdf} className="gap-2" data-testid="download-pdf-button">
            <FileText size={14} /> Print to PDF
          </Button>
          <Button onClick={downloadBrandedPdf} className="bg-[#0891b2] hover:bg-[#0e7490] text-white gap-2" data-testid="download-branded-pdf-button">
            <Download size={14} /> Branded PDF
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-sm p-0.5 w-fit">
        {[{ k: 'guides', label: 'How-To Guides', Icon: Book },
          { k: 'faq', label: 'FAQ', Icon: HelpCircle },
          { k: 'shortcuts', label: 'Quick Links', Icon: Sparkles }].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-sm transition-all ${
              tab === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid={`help-tab-${k}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === 'guides' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1 space-y-2" data-testid="guide-list">
            {GUIDES.map(g => {
              const GuideIcon = g.icon;
              const active = openGuide === g.id;
              return (
                <button key={g.id} onClick={() => setOpenGuide(g.id)}
                  className={`w-full text-left rounded-sm border transition-all ${active ? 'border-blue-400 bg-blue-50/60 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid={`guide-tab-${g.id}`}>
                  <div className="p-3 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-md bg-gradient-to-br ${g.color} text-white flex items-center justify-center flex-shrink-0`}>
                      <GuideIcon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-heading font-semibold text-slate-900 truncate">{g.title}</p>
                      <p className="text-xs text-slate-500">{g.steps.length} steps</p>
                    </div>
                    {active && <ChevronRight size={14} className="text-blue-500" />}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-sm p-6" data-testid="guide-detail">
            {(() => {
              const g = GUIDES.find(x => x.id === openGuide);
              const GuideIcon = g.icon;
              return (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-md bg-gradient-to-br ${g.color} text-white flex items-center justify-center`}>
                      <GuideIcon size={20} />
                    </div>
                    <h2 className="text-2xl font-heading font-semibold text-slate-900">{g.title}</h2>
                  </div>
                  <div className="space-y-5">
                    {g.steps.map((s, i) => (
                      <div key={i} className="border-l-2 border-slate-200 pl-4 pb-3">
                        <h3 className="text-base font-heading font-semibold text-slate-900 mb-2">{s.title}</h3>
                        <SafeText text={s.body} className="text-sm text-slate-600 leading-relaxed" />
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {tab === 'faq' && (
        <div className="space-y-3" data-testid="faq-list">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search FAQs (e.g. classification, encryption, MFA)…"
              className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid="faq-search"
            />
          </div>
          {filteredFaqs.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">
              No FAQs match "{search}". Try a different keyword.
            </div>
          )}
          {filteredFaqs.map((f, i) => (
            <details key={i} className="bg-white border border-slate-200 rounded-sm group" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer p-4 flex items-start justify-between gap-3 hover:bg-slate-50">
                <span className="text-sm font-heading font-medium text-slate-900 flex-1">{f.q}</span>
                <ChevronRight size={14} className="text-slate-400 flex-shrink-0 mt-0.5 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">{f.a}</div>
            </details>
          ))}
        </div>
      )}

      {tab === 'shortcuts' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="shortcuts-grid">
          {[
            { label: 'Upload data', href: '#upload', Icon: Upload, tone: 'from-emerald-500 to-teal-500' },
            { label: 'Fix corrections', href: '#corrections', Icon: Cog, tone: 'from-amber-500 to-orange-500' },
            { label: 'Supplier normalization', href: '#suppliers', Icon: Users, tone: 'from-purple-500 to-pink-500' },
            { label: 'Manage rules', href: '#rules', Icon: Layers, tone: 'from-blue-500 to-indigo-500' },
            { label: 'Privacy & GDPR', href: '#privacy', Icon: Shield, tone: 'from-rose-500 to-red-500' },
            { label: 'Connectors', href: '#connectors', Icon: Zap, tone: 'from-cyan-500 to-blue-500' },
          ].map((s) => {
            const I = s.Icon;
            return (
              <a key={s.label} href={s.href}
                className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-sm hover:border-slate-300 hover:shadow-sm transition-all"
                data-testid={`shortcut-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={`w-10 h-10 rounded-md bg-gradient-to-br ${s.tone} text-white flex items-center justify-center flex-shrink-0`}>
                  <I size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-heading font-semibold text-slate-900">{s.label}</p>
                  <p className="text-xs text-slate-500">Jump to section</p>
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};
